import React, { useState, useRef, useEffect } from 'react';
import { startOTA } from '../../../api/devices';
import { useSelector } from 'react-redux';
import image from '../../../assets/clone.svg';

const DeleteDeviceButton = ({ deviceId, removeDevice }) => {
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const buttonRef = useRef(null);
  const confirmationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        confirmationRef.current &&
        !confirmationRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsConfirmationVisible(false);
      }
    };

    if (isConfirmationVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isConfirmationVisible]);

  const handleDeleteClick = () => {
    setIsConfirmationVisible(true);
  };

  const handleConfirm = () => {
    removeDevice(deviceId);
    setIsConfirmationVisible(false);
  };

  const handleCancel = () => {
    setIsConfirmationVisible(false);
  };

  return (
    <>
      <button
        id="deleteDeviceBtn"
        ref={buttonRef}
        className="btn-primary w-full sm:w-auto bg-red-50 hover:bg-red-200 xl:w-fit"
        onClick={handleDeleteClick}
      >
        Удалить
      </button>

      {isConfirmationVisible && (
        <div className="fixed inset-0 z-50 lg:left-auto top-auto flex items-center justify-center ">
          <div
            ref={confirmationRef}
            className="max-w-md rounded-lg bg-white border p-2 shadow-lg"
          >
            <p className="p-2 text-center text-lg font-medium text-gray-800">
              Удалить устройство?
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="btn-primary w-24 bg-red-100 hover:bg-red-200"
                onClick={handleConfirm}
              >
                Да
              </button>
              <button
                className="btn-primary w-24 bg-gray-100 hover:bg-gray-200"
                onClick={handleCancel}
              >
                Нет
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CloneDeviceButton = ({ deviceId, cloneDevice }) => {
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const buttonRef = useRef(null);
  const confirmationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        confirmationRef.current &&
        !confirmationRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsConfirmationVisible(false);
      }
    };

    if (isConfirmationVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isConfirmationVisible]);

  const handleCloneClick = () => {
    setIsConfirmationVisible(true);
  };

  const handleConfirm = () => {
    cloneDevice(deviceId);
    setIsConfirmationVisible(false);
  };

  const handleCancel = () => {
    setIsConfirmationVisible(false);
  };

  return (
    <>
      <button
        id="cloneDeviceBtn"
        ref={buttonRef}
        className="btn-primary flex justify-center w-full sm:w-auto bg-blue-50 hover:bg-blue-100 xl:w-fit"
        onClick={handleCloneClick}
      >
        <img className="w-5 h-auto" src={image} alt="Yandex Smart Home" />
      </button>

      {isConfirmationVisible && (
        <div className="fixed inset-0 z-50 left-auto top-auto flex items-center justify-center">
          <div
            ref={confirmationRef}
            className="max-w-md rounded-lg bg-white border p-2 shadow-lg"
          >
            <p className="p-2 text-center text-lg font-medium text-gray-800">
              Клонировать устройство?
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="btn-primary w-24 bg-green-100 hover:bg-green-200"
                onClick={handleConfirm}
              >
                Да
              </button>
              <button
                className="btn-primary w-24 bg-gray-100 hover:bg-gray-200"
                onClick={handleCancel}
              >
                Нет
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ActionButton = ({ onClick, children, className = '', ...props }) => {
  return (
    <button
      className={`btn-primary w-full md:w-auto ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

function DeviceActions({
  removeDevice,
  cloneDevice,
  deviceId,
  isFresh,
  openYandex,
  openTrigger,
  toggleIde,
  openFirmwareInfoModal,
  openBuildModal,
  openEventLogModal,
  openDeviceHistoryModal,
}) {
  const { firmware } = useSelector((state) => state.firmware);
  const firmwareReady = firmware?.find((e) =>
    e.builds.some((build) => build.status === 'completed')
  );

  const handleOTA = () => {
    startOTA(firmwareReady.id);
  };

  return (
    <div className="w-full gap-4 shadow-xl drop-shadow-md border border-gray-200 rounded-2xl p-4">
      {/* Left Section */}
      <div className="flex flex-col justify-normal gap-1 xl:justify-between xl:flex-row">
        <ActionButton onClick={toggleIde}>▼ Редактор прошивки</ActionButton>
        <ActionButton
          onClick={(event) => {
            event.stopPropagation();
            openBuildModal(event, deviceId);
          }}
        >
          Скомпилировать
        </ActionButton>
        <ActionButton
          onClick={(event) => {
            event.stopPropagation();
            openFirmwareInfoModal(event, deviceId);
          }}
        >
          Прошивки
        </ActionButton>
        <ActionButton
          onClick={(event) => {
            event.stopPropagation();
            openDeviceHistoryModal(event, deviceId);
          }}
        >
          Телеметрия
        </ActionButton>
        <ActionButton
          onClick={(event) => {
            event.stopPropagation();
            openEventLogModal(event, deviceId);
          }}
        >
          Журнал
        </ActionButton>
        <ActionButton onClick={openTrigger}>Триггеры</ActionButton>
        <ActionButton onClick={openYandex}>Яндекс</ActionButton>
        <CloneDeviceButton deviceId={deviceId} cloneDevice={cloneDevice} />
        <DeleteDeviceButton deviceId={deviceId} removeDevice={removeDevice} />
      </div>
    </div>
  );
}

export default DeviceActions;
